<template>
  <div class="actions-container d-flex flex-wrap gap-1">
    <template v-for="(action, index) in actions" :key="action.label + index">
      <GuDialog
        v-if="action.confirmation"
        v-model:open="dialogOpen[index]"
      >
        <GuDialogTrigger as-child>
          <GuButton
            :icon="action.icon"
            :variant="action.style || 'secondary'"
            outline
            size="sm"
            :disabled="actionInProgress || isDisabled(action)"
          >
            {{ action.label }}
          </GuButton>
        </GuDialogTrigger>

        <GuDialogContent>
          <template #title>
            <h5 class="modal-title fs-18">{{ action.label }}</h5>
          </template>

          <p v-if="containsHtml(action.confirmation)" v-html="sanitizeHtml(action.confirmation)" />
          <p v-else>{{ action.confirmation }}</p>

          <template #footer>
            <GuDialogClose as-child>
              <GuButton variant="secondary" :disabled="actionInProgress">
                Cancel
              </GuButton>
            </GuDialogClose>

            <GuButton
              :variant="action.style || 'primary'"
              :loading="actionInProgress && runningIndex === index"
              :disabled="actionInProgress"
              @click="confirmAction(action, index)"
            >
              Confirm
            </GuButton>
          </template>
        </GuDialogContent>
      </GuDialog>

      <GuButton
        v-else-if="action.ruleId"
        :icon="action.icon"
        :variant="action.style || 'secondary'"
        outline
        size="sm"
        :loading="actionInProgress && runningIndex === index"
        :disabled="actionInProgress || isDisabled(action)"
        @click="executeAction(action, index)"
      >
        {{ action.label }}
      </GuButton>

      <a
        v-else-if="action.url"
        :class="['btn', 'btn-sm', `btn-outline-${action.style || 'secondary'}`]"
        :href="action.url"
        :title="action.tooltip"
      >
        <i v-if="action.icon" :class="['bx', action.icon]" aria-hidden="true" />
        <span v-if="action.label" class="ms-1">{{ action.label }}</span>
      </a>
    </template>
  </div>
</template>

<script setup>
import { computed, reactive, ref, toRefs } from 'vue'

import { GuButton } from '@shared/gu-components/button'
import { GuDialog, GuDialogClose, GuDialogContent, GuDialogTrigger } from '@shared/gu-components/dialog'
import { useToast } from '@shared/gu-components/toast'

import { triggerRule } from '@customers/pge/api/triggerRule.js'
import { applyActionResponse, sanitizeHtml, containsHtml, isLastAdminRemoveBlocked } from '@customers/pge/api/ruleResponse.js'

const props = defineProps({
  params: {
    type: Object,
    default: () => ({}),
  },
})
const { params } = toRefs(props)

const { toast } = useToast()

const actionInProgress = ref(false)
const runningIndex = ref(null)
const dialogOpen = reactive({})

// PG&E rule data uses lowercase keys (`ruleId`, `label`, ...) but the legacy
// Vue2 mocks/data sources use Capitalized keys (`RuleId`, `Label`, `URL`).
// Normalize so the template can rely on lowercase.
const actions = computed(() => {
  const value = params.value.data?.Actions
  if (!Array.isArray(value)) return []
  return value.map((a) => ({
    ruleId: a.ruleId ?? a.RuleId,
    label: a.label ?? a.Label,
    icon: a.icon ?? a.Icon,
    style: a.style ?? a.Style,
    tooltip: a.tooltip ?? a.Tooltip,
    url: a.url ?? a.URL,
    ruleParams: a.ruleParams ?? a.RuleParams ?? {},
    confirmation: a.confirmation ?? a.Confirmation,
  }))
})

// Block one-admin-removal at the client. Backend should enforce too, but the
// guard prevents a confusing failure-after-confirm experience.
function isDisabled (action) {
  return isLastAdminRemoveBlocked(action, params.value.data, params.value.context)
}

async function executeAction (action, index) {
  if (!action?.ruleId) return
  if (isDisabled(action)) return
  actionInProgress.value = true
  runningIndex.value = index

  const ruleParams = {
    rowId: params.value.data?.rowId ?? params.value.data?.ID ?? params.value.data?.id,
    ...(action.ruleParams || {}),
  }

  try {
    const response = await triggerRule(action.ruleId, ruleParams)
    // First try to apply structured commands from the rule response
    // (UPDATE_FIELD_VALUE / UPDATE_ROW / REMOVE_ROW). If the rule didn't return
    // any, fall back to a refetch so the grid reflects the new state.
    const handled = applyActionResponse(response, params.value)
    if (!handled) {
      params.value.context?.refetch?.()
    }
    if (response?.notification) {
      toast({
        variant: response.notification.type || 'info',
        description: response.notification.message,
      })
    }
  } catch (ex) {
    console.error('PgeCompaniesActionsCellRenderer triggerRule failed', ex)
    toast({
      variant: 'danger',
      description: ex?.message || 'Action failed',
    })
  } finally {
    actionInProgress.value = false
    runningIndex.value = null
  }
}

async function confirmAction (action, index) {
  await executeAction(action, index)
  dialogOpen[index] = false
}
</script>

<style scoped>
.actions-container :deep(.btn) {
  white-space: nowrap;
}
</style>
